import Close from '@mdi/svg/svg/close.svg?react';
import classNames from 'classnames';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useRef, useState } from 'react';
import { Portal } from 'react-portal';
import styled from 'styled-components';
import { EMPTY_ARRAY, EMPTY_OBJ } from '../../constants';
import { Scrollbars } from '../../mixins';
import { COLOR, FONT_SIZE, SIZE } from '../../styles';
import { type MinMaxValue } from '../../utils';
import { a11yKey } from '../../utils/ComponentUtil';
import { usePopoverZIndex } from '../../utils/PopoverZIndexManager';
import { Button } from '..//Button';
import { Flex } from '..//Flex';
import { Mask } from '..//Mask';
import { MDIIcon } from '../MDIIcon';
import { type ModalProps, type ModalType } from './Modal.types';

// All modals get protalled into this div, with it's own stacking context,
// so modal z-indexes don't break popovers.
// This component manipulates the DOM
// If this is used inside a component which is conditional rendered,
// you need to wrap this component (<Modal>) in a <div>
// Otherwise, when that parent component is being unmounted,
// <Modal> can't be found as a child node and cause error

const CLICK_DELAY = 200;
const MODAL_TARGET_ID = 'modal-container';

export const Modal: React.FC<ModalProps> = (props) => {
  const {
    /** Whether the modal will auto focus on an element in the modal.
     *  This can be override by the focusOnPrimary prop.
     *  If true, the modal will auto focus on the close button or the primary
     *  element specified by the focusOnPrimary prop.
     *  If false, the modal will not auto focus on any element unless user
     *  specifed the focusOnPrimary prop.
     */
    autoFocus = true,
    actions = EMPTY_ARRAY as any[],
    additionalHeaderInfo,
    bodyStyle,
    canConfirm = false,
    centerActions,
    children,
    className = '',
    closeOnOutsideClick = false,
    colorMode = 'light',
    contentType,
    dataTestId = 'modal',
    focusOnPrimary = false,
    footerChildren,
    headerActions = EMPTY_ARRAY as any[],
    isLoading = false,
    isOpen = false,
    message,
    modalBodyRef,
    noHeader = false,
    onClose,
    onConfirm,
    style = EMPTY_OBJ,
    subTitle,
    title,
    titleIcon,
    titleIconClass,
    type = 'narrow',
    eventPlayerBuiltInModal,
    closeButton = true,
    modalWrapperClassName = '',
    portalTargetId,
    dataIntercomTarget,
    autoWidth,
  } = props;

  // eslint-disable-next-line
  // @ts-ignore
  const { zIndex } = usePopoverZIndex(isOpen);
  const closeRef = useRef(null);
  const [isButtonFocused, setButtonFocusState] = useState(false);
  const [isDragging, setDraggingState] = useState(false);
  const [isInit, setInitState] = useState(false);

  const dragTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    // eslint-disable-next-line
    // @ts-ignore
    let previouslyFocused = [];
    const actionRefs: any = {};

    const setListener = () => {
      document.addEventListener('keydown', keyDown);
    };

    const saveFocusedElement = () => {
      const focused = document.activeElement;

      if (!focused) {
        return;
      }

      // Save the focused element
      previouslyFocused.push(focused);

      // Save the parent, as a backup
      if (focused.parentElement) {
        previouslyFocused.push(focused.parentElement);
      }

      // Save the first <a> or <button> of a parent .action element,
      // as a second backup.
      if (focused.closest('.action')) {
        // eslint-disable-next-line
        // @ts-ignore
        previouslyFocused.push(focused.closest('.action').querySelector('a, button'));
      }
    };

    const init = () => {
      setListener();
      // Save the current focused element, for when we close this
      saveFocusedElement();

      const isPrimaryInDOM = get(actionRefs, 'primary.focus');
      if (focusOnPrimary && isPrimaryInDOM) {
        actionRefs.primary.focus();
      } else if (closeRef.current && autoFocus) {
        // eslint-disable-next-line
        // @ts-ignore
        closeRef.current.focus();
      }

      setInitState(true);
    };

    const focusOnPrevious = () => {
      // eslint-disable-next-line
      // @ts-ignore
      for (const element of previouslyFocused) {
        if (element) {
          element.focus();
          break;
        }
      }
      previouslyFocused = [];
    };

    // eslint-disable-next-line
    // @ts-ignore
    const keyDown = (event) => {
      const { keyCode, skipModal } = event;

      if (!skipModal) {
        const confirmationKeys = [13];
        if (isButtonFocused) {
          confirmationKeys.push(32);
        }
        if (keyCode === 27) {
          // eslint-disable-next-line
          // @ts-ignore
          onClose();
          event.preventDefault();
          event.stopPropagation();
        } else if (canConfirm && confirmationKeys.includes(keyCode)) {
          // eslint-disable-next-line
          // @ts-ignore
          onConfirm?.();
          event.preventDefault();
          event.stopPropagation();
        }
      }
    };

    const removeListener = () => {
      document.removeEventListener('keydown', keyDown);
      focusOnPrevious();
    };

    if (isOpen && !isInit) {
      init();
    } else if (isInit && !isOpen) {
      setInitState(false);
    }

    return () => {
      removeListener();
    };
  }, [isOpen, isInit, focusOnPrimary, canConfirm, isButtonFocused, onClose, onConfirm]);

  const onButtonBlur = () => setButtonFocusState(false);

  const onButtonFocus = () => setButtonFocusState(true);

  const initDragging = () => {
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
    }
    dragTimeoutRef.current = setTimeout(() => {
      setDraggingState(true);
    }, CLICK_DELAY);
  };

  const endDragging = () => {
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
    }
    if (isDragging) {
      dragTimeoutRef.current = setTimeout(() => {
        setDraggingState(false);
      }, CLICK_DELAY);
    }
  };

  const handleOutsideClick = () => {
    if (!isDragging && closeOnOutsideClick) {
      // eslint-disable-next-line
      // @ts-ignore
      onClose();
    }
  };

  const hasActions = !isEmpty(actions);

  const displayFooter = hasActions || !!footerChildren;

  const wrapperClass = classNames(
    'modal-wrapper',
    `mode-${colorMode}`,
    { open: isOpen },
    type,
    modalWrapperClassName,
  );

  const titleWrapperClass = classNames('title-wrapper', {
    'with-icon': titleIcon || titleIconClass,
  });

  const dialogClass = classNames('modal', className, {
    'footer-less': !displayFooter,
    'header-less': noHeader,
    'sub-title': subTitle,
    'auto-width': autoWidth,
  });

  const closeButtonId = eventPlayerBuiltInModal
    ? `${contentType}-modal-close-button`
    : 'modal-close-button';

  return (
    <Portal node={document && document.getElementById(portalTargetId || MODAL_TARGET_ID)}>
      <ModalWrapper
        data-intercom-target={dataIntercomTarget}
        colorMode={colorMode}
        type={type}
        className={wrapperClass}
        onClick={handleOutsideClick}
        onMouseDown={initDragging}
        onMouseUp={endDragging}
        style={{ ...style, zIndex }}
      >
        <Dialog
          className={dialogClass}
          open={isOpen}
          data-testid={isOpen ? dataTestId : undefined}
          onClick={(event: React.MouseEvent) => event.stopPropagation()}
        >
          {!noHeader && (
            <Header>
              <Flex className={titleWrapperClass} alignItems="center">
                {titleIconClass && <i className={`title-icon ${titleIconClass}`} />}
                {titleIcon}
                <div className="title-text-wrapper">
                  {title && <h1 className="title">{title}</h1>}
                  {subTitle && <h2 className="sub-title">{subTitle}</h2>}
                </div>
                {additionalHeaderInfo && (
                  <Flex alignItems="center" className="modal-additional-header-info">
                    {additionalHeaderInfo}
                  </Flex>
                )}
              </Flex>
              <section className="actions">
                {headerActions.map((action, key) => (
                  <button
                    disabled={action.disabled}
                    key={key}
                    data-testid={`${dataTestId}-${action.title}`}
                    className={`action ${action.active ? 'active' : ''}`}
                    onClick={action.onClick}
                    onKeyDown={a11yKey(action.onClick)}
                    tabIndex={0}
                  >
                    {action.icon && action.icon}
                    {action.className && <span className={`icon ${action.className}`} />}
                    {action.title && <span className="action-label">{action.title}</span>}
                  </button>
                ))}
                {closeButton && (
                  <button
                    id={closeButtonId}
                    data-testid={`${dataTestId}-close`}
                    title="Close"
                    className="close"
                    onClick={onClose}
                    onKeyDown={a11yKey(onClose)}
                    ref={closeRef}
                    tabIndex={0}
                  >
                    <MDIIcon icon={Close} />
                  </button>
                )}
              </section>
            </Header>
          )}
          <Body className="body" style={bodyStyle} ref={modalBodyRef}>
            {isLoading && <Mask left={0} position="absolute" top={0} />}
            {!isLoading && isOpen && children}
            {!isLoading && isOpen && !children && message && (
              <p className="solo-message">{message}</p>
            )}
            {!children && !message && <div />}
          </Body>
          {displayFooter && (
            // eslint-disable-next-line
            // @ts-ignore
            <Footer centerActions={centerActions}>
              {footerChildren}
              {hasActions && (
                <div className="actions-wrapper">
                  {
                    // eslint-disable-next-line
                    // @ts-ignore
                    actions.map((action, index) => (
                      <Button
                        key={`modal-button-${index}`}
                        className={action.className}
                        colorMode={colorMode === 'dark' ? 'dark-on-dark' : 'dark'}
                        dataTestId={action.dataTestId ?? `${dataTestId}-${action.text}`}
                        disabled={action.disabled || isLoading}
                        icon={action.icon}
                        onBlur={onButtonBlur}
                        onClick={action.onClick}
                        onFocus={onButtonFocus}
                        isPrimary={action.isPrimary}
                        text={action.text}
                        type={action.type}
                        tooltip={action.tooltip}
                      />
                    ))
                  }
                </div>
              )}
            </Footer>
          )}
        </Dialog>
      </ModalWrapper>
    </Portal>
  );
};

// Styled Components
const headerRowHeight = 36;
const subTitleHeight = 24;
const headerRowHeightWithSubtitle = headerRowHeight + subTitleHeight;
const footerRowHeight = headerRowHeight;
const sectionPadding = 16;
const dialogHeaderFooter = headerRowHeight + 2 * sectionPadding;
const dialogHeaderWithSubtitle = headerRowHeightWithSubtitle + 2 * sectionPadding;

const headerRowHeightPx = `${headerRowHeight}px`;
const subTitleHeightPx = `${subTitleHeight}px`;
const headerRowHeightWithSubtitlePx = `${headerRowHeightWithSubtitle}px`;
const footerRowHeightPx = `${footerRowHeight}px`;
const sectionPaddingPx = `${sectionPadding}px`;
const dialogHeaderFooterPx = `${dialogHeaderFooter}px`;
const dialogHeaderWithSubtitlePx = `${dialogHeaderWithSubtitle}px`;
const modalRows = '1fr 90% 1fr';
const narrowSize = '480px';
const smallSize = '520px';
const mediumSize = '640px';
const wideSize = '800px';
const extraWideSize = '900px';
const doubleWideSize = '1350px';
const fullWidthSize = '90vw';
const fullScreenSize = '100vw';

const getGridTemplate = (type: ModalType = 'default'): string => {
  const sizeMap: Record<ModalType, [MinMaxValue, MinMaxValue]> = {
    default: [narrowSize, mediumSize],
    narrow: [narrowSize, narrowSize],
    medium: [smallSize, mediumSize],
    wide: [mediumSize, wideSize],
    'extra-wide': [wideSize, extraWideSize],
    'double-wide': [wideSize, doubleWideSize],
    'full-width': [wideSize, fullWidthSize],
    'full-screen': [wideSize, fullScreenSize],
  };

  const [minSize, maxSize] = sizeMap[type];
  return `1fr minmax(${minSize}, ${maxSize}) 1fr`;
};

const ModalWrapper = styled('section').withConfig({
  shouldForwardProp: (prop: any) => !['colorMode'].includes(prop),
})<{
  colorMode?: 'light' | 'dark';
  type?: ModalType;
}>`
  align-content: center;
  align-items: center;
  background: ${(props) =>
    props.colorMode === 'dark' ? 'rgba(93, 93, 93, 0.8)' : COLOR['modal-overlay']};
  bottom: 0;
  display: none;
  font-family: inherit;
  grid-template-areas: '. . .' '. modal .' '. . .';
  grid-template-rows: ${modalRows};
  justify-items: stretch;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 10000;

  grid-template-columns: ${(props) => getGridTemplate(props.type)};

  &.full-screen.modal-wrapper {
    grid-template-columns: 1fr minmax(${wideSize}, ${fullScreenSize}) 1fr;
    grid-template-rows: 0 100% 0;
    background: none;

    dialog.modal {
      height: 100%;
      background: rgba(0, 0, 0, 0.9);
    }
  }

  &.open {
    display: grid;
  }

  ${(props) =>
    props.colorMode === 'dark'
      ? `
  dialog.modal {
    background: ${COLOR['dark-gray-dark']};
    color: ${COLOR['white']};

    > header {
      border-bottom: 1px solid rgba(206, 206, 206, 0.2);

      .icon,
      .icon-sprite {
        fill: ${COLOR['white']};
      }

      .title-wrapper,
      .title,
      .sub-title {
        color: ${COLOR['white']};
      }
    }

    > section.body {
      color: ${COLOR['white']};
    }

    > footer {
      border-top: 1px solid rgba(206, 206, 206, 0.2);
    }

    .actions > button {
      color: ${COLOR['white']};

      &:hover,
      &:focus {
        background: ${COLOR['primary-light']};
        color: ${COLOR['turquoise-darker']};
      }
    }
  }`
      : ``}
`;

const Dialog = styled.dialog`
  background: ${COLOR['white']};
  border-style: none;
  box-shadow:
    rgba(0, 0, 0, 0.25) 0px 14px 45px,
    rgba(0, 0, 0, 0.22) 0px 10px 18px;
  color: inherit;
  display: grid;
  grid-area: modal;
  grid-template-areas: 'header' 'body' 'footer';
  grid-template-columns: 100%;
  grid-template-rows: ${dialogHeaderFooterPx} 1fr ${dialogHeaderFooterPx};
  height: auto;
  margin: auto;
  max-height: 100%;
  padding: 0;
  position: relative;
  width: 100%;

  &.header-less {
    grid-template-rows: 0 1fr 68px;
  }

  &.footer-less {
    grid-template-areas: 'header' 'body';
    grid-template-columns: 100%;
    grid-template-rows: ${dialogHeaderFooterPx} 1fr;

    &.header-less {
      grid-template-rows: 32px 1fr;
    }
  }

  &:focus {
    outline: none;
  }

  &.sub-title {
    grid-template-rows: ${dialogHeaderWithSubtitlePx} 1fr ${dialogHeaderFooterPx};

    &.footer-less {
      grid-template-rows: ${dialogHeaderWithSubtitlePx} 1fr;
    }

    > header {
      height: ${headerRowHeightWithSubtitlePx};

      .sub-title,
      .title {
        max-width: calc(100% - ${headerRowHeightWithSubtitlePx + sectionPaddingPx});
      }
    }
  }

  &.auto-width {
    width: auto;
  }
`;
const Header = styled.header`
  box-sizing: content-box;
  grid-area: header;
  height: ${headerRowHeightPx};
  padding: ${sectionPaddingPx};

  .title-wrapper {
    max-width: calc(100% - ${headerRowHeightPx} - ${sectionPaddingPx});

    .title-text-wrapper {
      flex-grow: 1;
      width: 100%;
    }

    &.with-icon {
      .title-text-wrapper {
        margin-left: 10px;
      }
    }
  }

  .sub-title,
  .title {
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .title {
    color: ${COLOR['dark-gray-dark']};
    font-size: ${FONT_SIZE['20']};
    font-weight: 300;
    line-height: ${headerRowHeightPx};
  }

  .sub-title {
    color: ${COLOR['dark-gray-light']};
    font-size: ${FONT_SIZE['14']};
    font-weight: 500;
    line-height: ${subTitleHeightPx};
  }

  .actions {
    justify-content: flex-start;
    position: absolute;
    right: 0;
    top: 0;

    &,
    > button {
      align-items: center;
      display: flex;
    }

    > button {
      background: none;
      border: none;
      box-sizing: content-box;
      color: ${COLOR['textColor']};
      cursor: pointer;
      height: ${headerRowHeightPx};
      justify-content: center;
      min-width: ${headerRowHeightPx};
      padding: ${sectionPaddingPx};

      &.active {
        background: ${COLOR['light-gray-light']};
        color: ${COLOR['dark-gray']};
      }

      &:hover,
      &:focus {
        background: ${COLOR['primary']};
        color: ${COLOR['white']};
        outline: none;

        .icon,
        .icon-sprite {
          fill: ${COLOR['white']};
        }
      }

      &.action {
        .action-label {
          font-family: inherit;
          font-size: ${FONT_SIZE['14']};
          padding-left: ${SIZE['4']};
        }
      }

      .icon {
        font-size: calc(${headerRowHeightPx} - 8px);
        fill: ${COLOR['textColor']};
      }
    }
  }
`;
const Body = styled.section`
  ${Scrollbars({
    width: '8px',
    height: '8px',
    border: '0.5px',
    foregroundColor: 'rgba(150, 150, 150, 0.5)',
    backgroundColor: 'transparent',
  })};
  color: ${COLOR['text']};
  grid-area: body;
  margin-top: 1px;
  min-height: 120px;
  overflow-y: auto;
  padding: 0 ${sectionPaddingPx};
  position: relative;

  &,
  p,
  li {
    font-size: ${FONT_SIZE['14']};
    font-weight: 300;
  }

  p {
    margin-bottom: ${SIZE['20']};

    &.solo-message {
      font-size: ${FONT_SIZE['18']};
      line-height: 1.4;
      padding: ${SIZE['16']} ${SIZE['40']};
      text-align: center;
    }
  }

  .accordion-container .accordion-item header {
    margin-left: ${SIZE['16']};
    margin-right: ${SIZE['16']};
  }
`;

const Footer = styled('footer').withConfig({
  shouldForwardProp: (prop) => !['centerActions'].includes(prop),
})<{ centerActions: boolean }>`
  box-sizing: content-box;
  display: flex;
  grid-area: footer;
  height: ${footerRowHeightPx};
  justify-content: space-between;
  padding: ${sectionPaddingPx};

  .actions-wrapper {
    ${({ centerActions }) => {
      return centerActions
        ? 'width: 100%; display: flex; justify-content: center; align-items: center;'
        : 'margin-left: auto;';
    }}

    button,
    .button {
      display: inline-block;
      margin-left: 10px;

      &.left-aligned {
        margin-left: 0;
        margin-right: auto;
      }
    }
  }
`;

// Display Name
Modal.displayName = 'Modal';

export default Modal;
